import { LitElement, html, property } from 'lit-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { contextualize, cutlassEvent } from '@polleverywhere/cutlass'
import { register } from '@polleverywhere/hmr-custom-element'
import '@polleverywhere/pe-button'
import '@polleverywhere/pe-graphic'
import './style.scss'

const MAX_TITLE_LENGTH = 32
const MAX_CONTENT_LENGTH = 128

const iconTypesMap = {
  error: 'critical',
  info: 'info',
  success: 'success',
  warning: 'alert'
}

/**
 * Notification represents a single notification to display containing a
 * notification with a required type and title. Additional content is optional.
 * The notification may contain a dismiss and/or an action CTA button,
 * both which emits events for consumers to listen to.
 *
 * @element pe-notification
 *
 * @fires click:action - Fires when the action CTA button is clicked
 * @fires click:dismiss - Fires when the dismiss button is clicked
 *
 * @prop {number} autoDismiss - Number of milliseconds to wait before this
 *  notification auto-dismisses itself. Defaults to 0, which turns
 *  auto-dismissing off.
 * @prop {string} action - If provided, adds a CTA with the given label that
 *  then emits a custom event when clicked.
 * @prop {string} content - If provided, adds a description below the title
 * @prop {string} title - Title is required
 * @prop {string} type - Type is required and is one of four values that
 *  specifies the color of the notification: [error,info,success,warning]
 *
 * @cssprop --pe-notification-background-color - Set background color
 * @cssprop --pe-notification-color - Set foreground color
 */
export default class Notification extends LitElement {
  static get tagName () { return 'pe-notification' }

  /* Use with caution. Do not dismiss notifications that require user input. */
  @property({ type: Number, attribute: 'auto-dismiss' })
  autoDismiss = 0

  @property({ type: String })
  action

  @property({ type: String })
  content

  @property({ type: String })
  title

  @property({ type: String })
  type = 'info'

  constructor () {
    super()
    contextualize(this, this.tagName)
  }

  /**
   * Holds reference to the delayed dismiss timer so we can clear it.
   * @type {number}
   */
  _autoDismissTimerId

  disconnectedCallback () {
    super.disconnectedCallback()
    window.clearTimeout(this._autoDismissTimerId)
  }

  updated (changedMap) {
    if (changedMap.has('autoDismiss') && this.autoDismiss) {
      // Auto dismiss this notification after some time delay.
      delayedDismiss.call(this, this.autoDismiss)
    }
  }

  createRenderRoot () { return this }

  render () {
    if (!iconTypesMap[this.type]) {
      throw new Error(`Invalid notification type '${this.type}'. Valid names are [${Object.keys(iconTypesMap).join(',')}]`)
    }

    const role = ['info', 'success'].includes(this.type) ? 'status' : 'alert'
    const typeMods = Object.fromEntries(
      Object.keys(iconTypesMap).map(type => [
        type,
        type === this.type
      ])
    )

    return html`
      <aside class=${bem({ ...typeMods })} role=${role} aria-label=${this.type}>
        <div class=${bem('highlight')}></div>
        <pe-graphic
          class=${bem('icon')}
          name=${iconTypesMap[this.type]}
          type='icon'
        ></pe-graphic>
        <div class=${bem('content-container')}>
          ${renderTitle.call(this)}
          ${renderContent.call(this)}
          <div class=${bem('controls')}>
            <pe-button
              @click=${this.dismiss}
              label='Dismiss'
              name='dismiss'
              type='link'
            ></pe-button>
            ${renderAction.call(this)}
          </div>
        </div>
      </aside>
    `
  }

  dismiss () {
    dispatchEvent.call(this, 'dismiss')
    this.remove()
  }
}

function delayedDismiss (delay) {
  window.clearTimeout(this._autoDismissTimerId)
  this._autoDismissTimerId = window.setTimeout(() => this.dismiss(), delay)
}

function dispatchEvent (type) {
  this.dispatchEvent(new CustomEvent(`click:${type}`, {
    bubbles: true,
    detail: {
      // Be reserved here and carefully consider if consumers actually need any
      // more detail info before adding it.
    }
  }))
  cutlassEvent(this, type)
}

function handleAction (e) {
  const actionType = e.currentTarget.name
  dispatchEvent.call(this, actionType)

  // Remove notification after action. We intentionally do not call "dismiss"
  // here since that represents a different action in and of itself.
  this.remove()
}

function renderTitle () {
  if (!this.title) return

  const title = this.title.substring(0, MAX_TITLE_LENGTH)
  return html`<div class=${bem('title')}>${title}</div>`
}

function renderContent () {
  if (!this.content) return

  const content = this.content.substring(0, MAX_CONTENT_LENGTH)
  return html`<p class=${bem('content')}>${content}</p>`
}

function renderAction () {
  if (!this.action) return

  return html`
    <pe-button
      @click=${handleAction}
      label=${this.action}
      name='action'
      type='link'
    ></pe-button>
  `
}

const bem = bemBlock(Notification.tagName)
register(module, Notification.tagName, Notification)
